import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardActions, Button, Typography, Stack, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ContactsIcon from '@mui/icons-material/Contacts';
import TaskIcon from '@mui/icons-material/Task';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useOutletContext } from 'react-router-dom';
import PhoneIcon from '@mui/icons-material/Phone';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import HelpIcon from '@mui/icons-material/Help';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';

const OnboardingCard = ({ title, description, example, icon }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [shouldShowMore, setShouldShowMore] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      const rawLineHeight = window.getComputedStyle(textRef.current).lineHeight;
      const lineHeight = rawLineHeight === 'normal' ? 20 : parseInt(rawLineHeight, 10);
      const height = textRef.current.clientHeight;
      setShouldShowMore(height > lineHeight * 5);
    }
  }, [example]);

  return (
    <Card sx={{
      height: '100%',
      minHeight: '50vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#00000020',
      borderRadius: '1.5rem',
      color: 'white',
      width: { xs: '85vw', sm: '70vw', md: '400px' },
      minWidth: { xs: '85vw', sm: '70vw', md: '400px' },
      flexShrink: 0,
      margin: '0 0.5rem',
      scrollSnapAlign: 'center'
    }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          {icon}
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {description}
        </Typography>
        <Typography variant="body2" sx={{ fontStyle: 'italic', opacity: 0.8 }}>
          {t('example')}:
        </Typography>
        <div
          ref={textRef}
          style={{
            overflow: 'hidden',
            maxHeight: expanded ? 'none' : "16vh",
            transition: 'max-height 0.3s ease-out',
            marginBottom: '0.5rem'
          }}
        >
            {
                (typeof example === 'string' ? example.split('\n') : [String(example)]).map((line, index) => (
                    <Typography key={index} variant="body2" sx={{ opacity: 0.8, marginBottom: '0.5rem' }}>
                        {line}
                    </Typography>
                ))
            }
        </div>
        {shouldShowMore && (
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            startIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            sx={{ color: 'white', textTransform: 'none' }}
          >
            {expanded ? t('showLess') : t('showMore')}
          </Button>
        )}
      </CardContent>
      <CardActions sx={{ marginTop: 'auto', padding: 2 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={() => navigate("/app", { state: { example: example } })}
        >
          {t('tryIt')}
        </Button>
      </CardActions>
    </Card>
  );
};

const Onboarding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [currentCard, setCurrentCard] = useState(0);
  const { setHide } = useOutletContext() ?? {};
  const scrollTimeoutRef = useRef(null);

  useEffect(() => {
    setHide?.(true);
    return () => {
      setHide?.(false);
    };
  }, [setHide]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const getIconForKey = (key) => {
    switch (key) {
      case 'contactAndCompany':
        return <ContactsIcon sx={{ fontSize: 30 }} />;
      case 'dealsPipeline':
        return <AccountBalanceIcon sx={{ fontSize: 30 }} />;
      case 'tasksAndNotes':
        return <TaskIcon sx={{ fontSize: 30 }} />;
      case 'email':
        return <EmailIcon sx={{ fontSize: 30 }} />;
      case 'meeting':
        return <MeetingRoomIcon sx={{ fontSize: 30 }} />;
      case 'call':
        return <PhoneIcon sx={{ fontSize: 30 }} />;
      case 'allInOne':
        return <AllInclusiveIcon sx={{ fontSize: 30 }} />;
      default:
        return <HelpIcon sx={{ fontSize: 30 }} />;
    }
  };

  const examplesKeys = [
    "contactAndCompany",
    "dealsPipeline",
    "tasksAndNotes",
    "email",
    "meeting",
    "call",
    "allInOne"
  ];

  const examples = examplesKeys.map((key) => ({
    title: t(`onboardingExamples.${key}.title`),
    description: t(`onboardingExamples.${key}.description`),
    example: t(`onboardingExamples.${key}.example`),
    icon: getIconForKey(key)
  }));

  const scrollToCard = useCallback((index) => {
    const clampedIndex = Math.max(0, Math.min(index, examples.length - 1));
    if (scrollRef.current) {
      const targetCard = scrollRef.current.children[clampedIndex];
      if (targetCard) {
        targetCard.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
      setCurrentCard(clampedIndex);
    }
  }, [examples.length]);

  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      if (scrollRef.current) {
        const cardWidth = scrollRef.current.offsetWidth;
        const scrollPosition = scrollRef.current.scrollLeft;
        const newIndex = Math.round(scrollPosition / cardWidth);
        setCurrentCard(newIndex);
      }
    }, 100);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      scrollToCard(currentCard - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      scrollToCard(currentCard + 1);
    }
  }, [currentCard, scrollToCard]);

  return (
    <Stack spacing={3} sx={{ padding: '1rem', width: '100%', margin: 'auto' }}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h5" sx={{ color: 'white' }}>
            {t('welcomeToLabia')}
          </Typography>
        </Stack>
        <IconButton
          aria-label={t('close', 'Close')}
          sx={{ color: 'white' }}
          onClick={() => navigate("/app")}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Typography variant="body1" sx={{ color: 'white' }} gutterBottom>
        {t('onboardingDescription')}
      </Typography>

      <div
        role="region"
        aria-roledescription="carousel"
        aria-label={t('onboardingExamplesCarousel', 'Onboarding examples')}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <div
          aria-live="polite"
          aria-atomic="true"
          style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clipPath: 'inset(50%)', whiteSpace: 'nowrap' }}
        >
          {t('slideXofY', { current: currentCard + 1, total: examples.length, defaultValue: `Slide ${currentCard + 1} of ${examples.length}` })}
        </div>
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="hide-scrollbar"
          style={{
            width: '100%',
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            display: 'flex',
            WebkitOverflowScrolling: 'touch',
            padding: '1rem 0'
          }}
        >
          {examples.map((example, index) => (
            <OnboardingCard key={examplesKeys[index]} {...example} />
          ))}
        </div>
      </div>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="center"
      >
        <IconButton
          aria-label={t('previousSlide', 'Previous slide')}
          sx={{ color: 'white' }}
          onClick={() => scrollToCard(currentCard - 1)}
          disabled={currentCard === 0}
        >
          <ChevronLeftIcon />
        </IconButton>

        {examples.map((_, index) => (
          <button
            key={examplesKeys[index]}
            onClick={() => scrollToCard(index)}
            aria-label={t('goToSlide', { number: index + 1, defaultValue: `Go to slide ${index + 1}` })}
            aria-current={currentCard === index ? 'true' : undefined}
            style={{
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            <span style={{
              width: currentCard === index ? '24px' : '8px',
              height: '8px',
              borderRadius: '4px',
              backgroundColor: currentCard === index ? 'white' : 'rgba(255, 255, 255, 0.5)',
              transition: 'all 0.3s ease',
              display: 'block',
            }} />
          </button>
        ))}

        <IconButton
          aria-label={t('nextSlide', 'Next slide')}
          sx={{ color: 'white' }}
          onClick={() => scrollToCard(currentCard + 1)}
          disabled={currentCard === examples.length - 1}
        >
          <ChevronRightIcon />
        </IconButton>
      </Stack>
    </Stack>
  );
};

export default Onboarding;
